/**
 * Choosing which melds to lay down, when the hand can make the contract more than one
 * way. What goes down decides what stays in hand, so it is the player's call. Takes the
 * place of the piles while open, so it never covers the hand. scope.md §3.4 and §8.2.
 *
 * The player picks one set or run at a time rather than a whole combination, which is
 * far easier to read. Each pick narrows the ways still open; once only one is left, the
 * rest of it is forced and it goes down.
 */

import { useState } from 'react';
import type { Card, ProposedMeld } from '@garbage/shared';
import { isJoker } from '@garbage/shared';
import { meldKey } from '@garbage/engine';
import { CardFace } from './Card.js';
import type { PackId } from '../packs.js';
import { useT } from '../i18n.js';

export function DownChooser({
  options, hand, pack, onPick, onCancel,
}: {
  options: ProposedMeld[][];
  hand: readonly Card[];
  pack: PackId;
  onPick: (melds: ProposedMeld[]) => void;
  onCancel: () => void;
}) {
  const t = useT();
  const byId = new Map(hand.map((c) => [c.id, c]));
  const key = (meld: ProposedMeld) => meldKey(meld, hand);
  /** Keys of the melds picked so far, in the order picked. */
  const [picked, setPicked] = useState<string[]>([]);

  const open = options.filter((melds) => picked.every((k) => melds.some((m) => key(m) === k)));

  // Every meld still open that has not been picked, each offered once.
  const unique = new Map<string, ProposedMeld>();
  for (const melds of open) {
    for (const meld of melds) {
      const k = key(meld);
      if (!picked.includes(k) && !unique.has(k)) unique.set(k, meld);
    }
  }
  // All the sets together, then all the runs, so like sits with like; longest runs first.
  const choices = [...unique].sort(([, a], [, b]) => (a.kind === b.kind
    ? b.cardIds.length - a.cardIds.length
    : a.kind === 'set' ? -1 : 1));

  function pick(k: string) {
    const next = [...picked, k];
    const remaining = options.filter((melds) => next.every((p) => melds.some((m) => key(m) === p)));
    if (remaining.length === 1) {
      onPick(remaining[0]!);
      return;
    }
    setPicked(next);
  }

  const pickedMelds = picked
    .map((k) => open[0]?.find((m) => key(m) === k))
    .filter((m): m is ProposedMeld => Boolean(m));

  const cards = (meld: ProposedMeld) => meld.cardIds.map((id) => {
    const card = byId.get(id);
    return card
      ? <CardFace key={id} card={card} size="small" marked={isJoker(card)} pack={pack} />
      : null;
  });
  const label = (meld: ProposedMeld) => (meld.kind === 'set' ? t('meld.set') : t('meld.run'));

  return (
    <div className="flex flex-col gap-2 px-3 pb-1" data-testid="down-chooser">
      <p className="text-center text-sm font-bold text-sun">
        {picked.length === 0 ? t('down.pick') : t('down.pickNext')}
      </p>
      {pickedMelds.length > 0 && (
        <div className="flex flex-wrap items-center justify-center gap-2" data-testid="down-picked">
          {pickedMelds.map((meld) => (
            <span key={key(meld)} className="flex shrink-0 items-center gap-0.5 opacity-70">
              <span className="pr-1 text-[10px] font-bold text-bone/60">✓ {label(meld)}</span>
              {cards(meld)}
            </span>
          ))}
        </div>
      )}
      {/* A tall list scrolls inside itself; the hand below stays put. The padding is
          for the rings: a scroller clips anything drawn outside its box. */}
      <div className="flex max-h-[40dvh] flex-col gap-2 overflow-y-auto overscroll-contain p-1">
        {choices.map(([k, meld], index) => (
          <button
            key={k}
            type="button"
            data-testid={`down-option-${index}`}
            onClick={() => pick(k)}
            className="flex shrink-0 items-center gap-0.5 rounded-2xl bg-black/25 px-2 py-1.5 text-left
                       ring-2 ring-sun/60 active:scale-[0.98]"
          >
            <span className="w-9 shrink-0 text-[10px] font-bold text-bone/60">{label(meld)}</span>
            {cards(meld)}
          </button>
        ))}
      </div>
      <div className="flex gap-2">
        {picked.length > 0 && (
          <button
            type="button"
            data-testid="down-back"
            onClick={() => setPicked(picked.slice(0, -1))}
            className="min-h-12 flex-1 rounded-xl bg-white/10 text-sm font-bold text-bone/80"
          >
            {t('common.back')}
          </button>
        )}
        <button
          type="button"
          data-testid="down-cancel"
          onClick={onCancel}
          className="min-h-12 flex-1 rounded-xl bg-white/10 text-sm font-bold text-bone/80"
        >
          {t('common.cancel')}
        </button>
      </div>
    </div>
  );
}
